"use client";

import RangeDropdown from "@/components/atoms/range-dropdown";
import { Switch } from "@/components/ui/switch";
import { Locales } from "@/lib/i18n";
import { __ } from "@/lib/i18n/functions";
import { cn } from "@/lib/utils";
import { useProductVariant } from "@/hooks/useProductVariant";
import { useCheckoutStore } from "@/stores/checkout-store";
import { MedusaMetada } from "@/types";
import { StoreProduct } from "@medusajs/types";

type ProductRangeProps = {
  locale: Locales;
  medusaId: string;
  className?: string;
  medusaProduct: StoreProduct | undefined;
  // This allows the hook to seed the initial surface from the ?surface= URL param.
  seedFromUrl?: boolean;
};

const ProductRange = ({ locale, medusaId, className, medusaProduct, seedFromUrl = false }: ProductRangeProps) => {
  const metadata = medusaProduct?.metadata as MedusaMetada;

  // Priority: URL param (PDP only) → persisted store → cart → product min
  const { resolvedSurface, setVariant, customization } = useProductVariant(medusaProduct, medusaId, { seedFromUrl });

  const setProductCustomization = useCheckoutStore((s) => s.setProductCustomization);

  const handleSurfaceChange = (value: number) => setVariant(value);
  const handleHeightChange = (value: string) => setProductCustomization(medusaId, { height: value });
  const handleSurfaceTypeChange = (value: string) => setProductCustomization(medusaId, { surfaceType: value });
  const handleSwitchChange = (field: "montage" | "tapis" | "jupe" | "escalier", checked: boolean) =>
    setProductCustomization(medusaId, { [field]: checked });

  const hasSurfaceOptions = metadata?.surface_min && metadata?.surface_max && metadata?.surface_step;
  const hasHeightOptions = metadata?.heights;
  const hasSurfaceTypeOptions = metadata?.surfaces;
  const hasSwitchOptions = metadata?.montage || metadata?.tapis || metadata?.jupe || metadata?.escalier;

  if (!hasSurfaceOptions && !hasHeightOptions && !hasSurfaceTypeOptions && !hasSwitchOptions) {
    return null;
  }

  return (
    <div>
      <header className={cn("mr-1 flex min-h-12.5 flex-wrap items-center justify-between border-t", className)}>
        <p className="font-cabinet-grotesk col-span-2 text-lg font-medium text-gray-900">
          {__("Customize your order", locale)}
        </p>
      </header>

      <div className="grid-cols-2 gap-x-3 gap-y-4 max-sm:space-y-4 sm:grid">
        {hasSurfaceOptions && (
          <RangeDropdown
            locale={locale}
            title={__("Surface", locale)}
            min={metadata.surface_min!}
            max={metadata.surface_max!}
            step={metadata.surface_step!}
            unit="m²"
            defaultValue={resolvedSurface ?? metadata.surface_min}
            onChange={(value) => handleSurfaceChange(parseInt(value))}
          />
        )}

        {hasHeightOptions && (
          <RangeDropdown
            locale={locale}
            title={__("Height", locale)}
            values={metadata.heights!}
            unit="cm"
            defaultValue={customization?.height}
            onChange={handleHeightChange}
          />
        )}

        {hasSurfaceTypeOptions && (
          <RangeDropdown
            locale={locale}
            title={__("Surface type", locale)}
            values={metadata.surfaces!}
            defaultValue={customization?.surfaceType}
            onChange={handleSurfaceTypeChange}
            unit="m²"
          />
        )}
      </div>

      {hasSwitchOptions && (
        <div className="mt-3.5 gap-x-3 gap-y-4 border-t pt-4 max-sm:space-y-4 sm:grid sm:grid-cols-2">
          <p className="font-cabinet-grotesk col-span-2 text-lg font-medium text-gray-900">
            {__("Need accessories?", locale)}
          </p>

          {metadata?.montage && (
            <Switch
              id={`assembly-${medusaId}`}
              label={__("Assembly", locale)}
              checked={customization?.montage ?? false}
              onCheckedChange={(checked) => handleSwitchChange("montage", checked)}
            />
          )}

          {metadata?.tapis && (
            <Switch
              id={`carpet-${medusaId}`}
              label={__("Carpet", locale)}
              checked={customization?.tapis ?? false}
              onCheckedChange={(checked) => handleSwitchChange("tapis", checked)}
            />
          )}

          {metadata?.jupe && (
            <Switch
              id={`skirt-${medusaId}`}
              label={__("Skirt", locale)}
              checked={customization?.jupe ?? false}
              onCheckedChange={(checked) => handleSwitchChange("jupe", checked)}
            />
          )}

          {metadata?.escalier && (
            <Switch
              id={`stairs-${medusaId}`}
              label={__("Stairs", locale)}
              checked={customization?.escalier ?? false}
              onCheckedChange={(checked) => handleSwitchChange("escalier", checked)}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default ProductRange;
